/**
 * byo-v4-confirm.js — stage 3, "Wrap It Up" (P5).
 *
 * Ported by name from `byo-stages-v3.jsx`: `ReviewPanel` (354), `FailBanner`
 * (324), `EditBanner` (332), `BagBanner` (337). DESIGN-SPEC.md §5.
 *
 * **No blocker card** (§5): what is missing is said by the bar hint and by the
 * inline errors on Personalise, not by a panel here. Do not add one back.
 *
 * D70 (Alan, 26 Sep 2026): a lime callout says what the step is for; how the
 * box is given is chosen here, and only here, as a two-way toggle with the
 * chosen side in lime; a purple card says how many Wrapt Rewards points the
 * box earns.
 *
 * D95 (Alan, 29 Sep 2026): the keepsake box comes with any box that fills five
 * spaces or more, however it is given (byo-v4-core.js), so its wrap is chosen
 * in a card of its own, not under "Gift In Person". Below five spaces nothing
 * here mentions it.
 *
 * D97 (Alan, 30 Sep 2026): the step no longer asks how the box is given. The
 * "How It’s Being Given" card and its toggle are gone; the keepsake card is
 * the only choice here. A new box is recorded as sent directly (byo-v4-submit.js).
 *
 * ES2020 module. View layer: assets/byo-v4-vendor.js (Preact + htm).
 */

import { html } from './byo-v4-vendor.js';
const { Icon, Btn, Pill, PointsCard, rewardPoints } = (window.BYOV4 = window.BYOV4 || {});
const { ShippingBar } = (window.BYOV4 = window.BYOV4 || {});
const { BoxItemRow } = (window.BYOV4 = window.BYOV4 || {});
const { WrapChooser, prettyDate } = (window.BYOV4 = window.BYOV4 || {});
/* Counts as words (D70), read at call time from byo-v4-core.js. */
const W = (n) => (window.BYO && window.BYO.Words ? window.BYO.Words(n) : String(n));

/** EditBanner — a lime strip while a cart bundle is being edited. §5.5. */
export function EditBanner({ b, onCancel }) {
  if (!b.editing) return null;
  return html`
    <div class="byo-v4-banner byo-v4-banner--edit">
      <${Icon} name="edit" size=${16} />
      <span><strong>Editing ${b.bundleName || 'This Box'}</strong> — change anything, then Update</span>
      <button type="button" class="byo-v4-banner__ghost" onClick=${onCancel}>Cancel</button>
    </div>
  `;
}

/** BagBanner — "Two boxes in your cart · you're building another". §5.5. */
export function BagBanner({ count, cartUrl = '/cart' }) {
  if (!count) return null;
  return html`
    <div class="byo-v4-banner byo-v4-banner--bag">
      <span><strong>${W(count)} box${count > 1 ? 'es' : ''} in your cart</strong> · you’re building another</span>
      <a class="byo-v4-banner__ghost" href=${cartUrl}>View Cart</a>
    </div>
  `;
}

/**
 * FailBanner — the sold-out card. §5.5. A stock rejection must keep the box
 * intact and offer a swap (BUILD-PLAN P6), so this never clears anything by
 * itself; the caller decides what "Swap It" removes.
 */
export function FailBanner({ fail, onSwap, onDismiss }) {
  if (!fail) return null;
  return html`
    <div class="byo-v4-fail">
      <div class="byo-v4-fail__h">${fail.title} just sold out</div>
      <div class="byo-v4-fail__s">Swap it for something else; the rest of your box is saved.</div>
      <div class="byo-v4-fail__btns">
        <${Btn} variant="white" size="sm" onClick=${onSwap}>Swap It<//>
        <${Btn} variant="ghost" size="sm" onClick=${onDismiss}>Not Now<//>
      </div>
    </div>
  `;
}

/** A section head: 15/700 with an underlined 13/600 "Edit" that jumps back. */
function Head({ title, onEdit }) {
  return html`
    <div class="byo-v4-rev__head">
      <h3 class="byo-v4-rev__h">${title}</h3>
      ${onEdit && html`<button type="button" class="byo-v4-rev__edit" onClick=${onEdit}>Edit</button>`}
    </div>
  `;
}

/** One 13px row per active touch — §5.2. */
function Touches({ b, config, onAddMessage }) {
  const box = b.box;
  const extras = config.extras || {};
  const price = (k, f) => b.money(extras[k] && typeof extras[k].price === 'number' ? extras[k].price : f);
  const rows = [];

  if (box.photo.on) {
    rows.push(
      html`<div class="byo-v4-trow" key="photo">
        <${Icon} name="camera" size=${14} />
        <span class="byo-v4-trow__t">
          ${box.photo.file
            ? html`${box.photo.file.name} <${Pill} tone="lime">Uploaded<//>`
            : html`<strong>Photo still needed</strong>`}
        </span>
        <span class="byo-v4-trow__p">${price('photo', 250)}</span>
      </div>`
    );
  }

  /* Only a box that already carries a song — the song is off (D70). */
  if (box.song.on) {
    rows.push(
      html`<div class="byo-v4-trow" key="song">
        <${Icon} name="music" size=${14} />
        <span class="byo-v4-trow__t">${box.song.text || html`<strong>Song still needed</strong>`}</span>
        <span class="byo-v4-trow__p">${price('song', 250)}</span>
      </div>`
    );
  }

  /* D70: no "Unsigned" here. There is no "From" field any more, so whether a
     message is signed is inside the message, where this cannot see it. */
  if (box.messageOn) {
    rows.push(
      html`<div class="byo-v4-trow" key="message">
        <${Icon} name="edit" size=${14} />
        <span class="byo-v4-trow__t">
          <em>“${box.message}”</em>
          ${box.from ? html` — From ${box.from}` : ''}
        </span>
        <span class="byo-v4-trow__p">Free</span>
      </div>`
    );
  }

  /* D121 (Alan): a box going without a message says so plainly, first, with
     the way back to add one. */
  if (!box.messageOn) {
    rows.unshift(
      html`<div class="byo-v4-nomsg" key="nomsg" role="note">
        <${Icon} name="edit" size=${16} />
        <span class="byo-v4-nomsg__t"><strong>No gift message</strong> It’ll arrive without a message card.</span>
        ${onAddMessage && html`<button type="button" class="byo-v4-nomsg__add" onClick=${onAddMessage}>Add One</button>`}
      </div>`
    );
  }

  if (box.date.on) {
    rows.push(
      html`<div class="byo-v4-trow" key="date">
        <${Icon} name="calendar" size=${14} />
        <span class="byo-v4-trow__t">Save-the-date sticker · open on ${prettyDate(box.date.value)}</span>
        <span class="byo-v4-trow__p">Free</span>
      </div>`
    );
  }

  if (!rows.length) return html`<div class="byo-v4-rev__none">None added — that’s fine too</div>`;
  return html`<div class="byo-v4-trows">${rows}</div>`;
}

/**
 * ReviewPanel — byo-stages-v3.jsx:354. DESIGN-SPEC.md §5.
 */
export function ConfirmStage({ b, config, wraps, showErrors, onEdit, onAddMessage, fail, onSwap, onDismissFail, onCancelEdit, cartCount }) {
  const box = b.box;
  const n = (b.units || []).length;
  const points = rewardPoints ? rewardPoints(b.total, config) : 0;

  return html`
    <div class="byo-v4-rev byo-v4-fill__inner">
      <${EditBanner} b=${b} onCancel=${onCancelEdit} />
      <${BagBanner} count=${cartCount} cartUrl=${(config.routes && config.routes.cart) || '/cart'} />
      <${FailBanner} fail=${fail} onSwap=${onSwap} onDismiss=${onDismissFail} />

      <div class="byo-v4-callout byo-v4-callout--block byo-v4-rev__callout">
        <${Icon} name="check" size=${18} stroke=${2.5} />
        <span><strong>Last look.</strong> Check it all looks right, then add it to your cart. You can still edit it from the cart.</span>
      </div>

      <div class="byo-v4-rev__grid">
        <div class="byo-v4-rev__col">
          <div class="byo-v4-card">
            <${Head} title="In The Box" onEdit=${() => onEdit('fill')} />
            <div class="byo-v4-rev__items">
              ${box.items.length === 0
                ? html`<div class="byo-v4-rev__none">Nothing in here yet</div>`
                : box.items.map((it) => html`<${BoxItemRow} key=${it.id} it=${it} b=${b} />`)}
            </div>
          </div>

          <div class="byo-v4-card">
            <${Head} title="Personal Touches" onEdit=${() => onEdit('personal')} />
            <${Touches} b=${b} config=${config} onAddMessage=${onAddMessage} />
          </div>

          ${/* D95, D97: the keepsake box, for any box of five spaces or more. */ ''}
          ${box.keepsake &&
          html`<div class="byo-v4-card byo-v4-rev__keepsake">
            <${Head} title="Keepsake Box" />
            <${WrapChooser} b=${b} wraps=${wraps} showErrors=${showErrors} />
          </div>`}
        </div>

        <div class="byo-v4-rev__col">
          <div class="byo-v4-card">
            <${Head} title="Price" />
            <div class="byo-v4-price">
              <div class="byo-v4-row byo-v4-row--top">
                <span>
                  <span class="byo-v4-ledger__k">Gift wrapping service</span>
                  <span class="byo-v4-ledger__note">Included in every box — no shipping surprises later</span>
                </span>
                <span>${b.money(config.base || 0)}</span>
              </div>
              <div class="byo-v4-row"><span>${n ? `${W(n)} item${n === 1 ? '' : 's'}` : 'No items'}</span><span>${b.money(b.itemsTotal)}</span></div>
              ${box.keepsake &&
              html`<div class="byo-v4-row"><span>Signature keepsake box</span><span><strong>Free</strong></span></div>`}
              ${b.extrasTotal > 0 &&
              html`<div class="byo-v4-row"><span>Personal touches</span><span>${b.money(b.extrasTotal)}</span></div>`}
              <div class="byo-v4-price__rule"></div>
              <div class="byo-v4-row byo-v4-price__total"><span>Total</span><span>${b.money(b.total)}</span></div>
            </div>

            ${PointsCard && html`<${PointsCard} points=${points} subject="box" class="byo-v4-rev__points" />`}

            <${ShippingBar} b=${b} class="byo-v4-rev__ship" />

            <div class="byo-v4-rev__multi">
              <${Icon} name="truck" size=${14} />
              <span>
                ${(config.copy && config.copy.multishipHint) ||
                'Sending to multiple people. Build a box each, then set the addresses in the cart.'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

/* Published for the siblings that used to `import` this file. See the
   section's loader: relative imports drop the ?v= and are served from a
   year-long CDN cache, so a sibling could be a build behind. */
Object.assign((window.BYOV4 = window.BYOV4 || {}), { BagBanner, ConfirmStage, EditBanner, FailBanner });
